import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import Layout from './components/Layout/Layout';
import ScrollToTop from './components/ScrollToTop/ScrollToTop';
import ComboModalHost from './components/cart/ComboModalHost';
import { preloadCriticalRoutes } from './utils/routePreload';
import { initGA } from './services/analytics';
import RouteTracker from './components/RouteTracker/RouteTracker';
import { Analytics } from '@vercel/analytics/react';
import './App.css';

// Lazy load pages for code splitting and faster initial load
const Home = lazy(() => import('./pages/Home/Home'));
const Shop = lazy(() => import('./pages/Shop/Shop'));
const ProductDetail = lazy(() => import('./pages/ProductDetail/ProductDetail'));
const CustomOrder = lazy(() => import('./pages/CustomOrder/CustomOrder'));
const About = lazy(() => import('./pages/About/About'));
const Contact = lazy(() => import('./pages/Contact/Contact'));
const Login = lazy(() => import('./pages/Auth/Login'));
const Register = lazy(() => import('./pages/Auth/Register'));
const Cart = lazy(() => import('./pages/Cart/Cart'));
const Checkout = lazy(() => import('./pages/Checkout/Checkout'));
const Combos = lazy(() => import('./pages/Combos/Combos'));
const OrderSuccess = lazy(() => import('./pages/OrderSuccess/OrderSuccess'));
const Profile = lazy(() => import('./pages/Profile/Profile'));
const Orders = lazy(() => import('./pages/Orders/Orders'));
const OrderTracking = lazy(() => import('./pages/Orders/OrderTracking'));
const TermsAndConditions = lazy(() => import('./pages/TermsAndConditions/TermsAndConditions'));
const ForgotPassword = lazy(() => import('./pages/Auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/Auth/ResetPassword'));
const Surprise = lazy(() => import('./pages/Surprise/Surprise'));
const AdminLayout = lazy(() => import('./components/Admin/AdminLayout'));
const AdminDashboard = lazy(() => import('./pages/Admin/AdminDashboard'));
const AdminProducts = lazy(() => import('./pages/Admin/AdminProducts'));
const AdminProductForm = lazy(() => import('./pages/Admin/AdminProductForm'));
const StaffRoute = lazy(() => import('./components/auth/StaffRoute'));

// Loading component shown while lazy components load
const PageLoader = () => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '60vh',
    gap: '20px'
  }}>
    <div style={{
      width: '50px',
      height: '50px',
      border: '4px solid #f3f3f3',
      borderTop: '4px solid #4A90E2',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }}></div>
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

function App() {
  // Initialize GA4 once on mount (no-op in development)
  // Preload critical routes after initial render
  useEffect(() => {
    initGA();
    preloadCriticalRoutes();
  }, []);

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ''}>
      <Router>
      {/* Tracks a GA4 page_view on every React Router navigation */}
      <RouteTracker />
      <AuthProvider>
        <CartProvider>
          <Toaster
            position="top-center"
            toastOptions={{
              duration: 3000,
              success: {
                style: {
                  background: '#22c55e',
                  color: '#fff',
                  fontWeight: '500',
                  fontSize: '14px',
                  borderRadius: '8px',
                  padding: '10px 16px',
                },
                iconTheme: {
                  primary: '#fff',
                  secondary: '#22c55e',
                },
              },
            }}
          />
          <ScrollToTop />
          <ComboModalHost />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route
                path="/admin"
                element={
                  <StaffRoute>
                    <AdminLayout />
                  </StaffRoute>
                }
              >
                <Route index element={<AdminDashboard />} />
                <Route path="products" element={<AdminProducts />} />
                <Route path="products/new" element={<AdminProductForm />} />
                <Route path="products/:id/edit" element={<AdminProductForm />} />
              </Route>

              <Route
                path="*"
                element={
                  <Layout>
                    <Suspense fallback={<PageLoader />}>
                      <Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="/shop" element={<Shop />} />
                        <Route path="/product/:id" element={<ProductDetail />} />
                        <Route path="/custom-order" element={<CustomOrder />} />
                        <Route path="/about" element={<About />} />
                        <Route path="/contact" element={<Contact />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/cart" element={<Cart />} />
                        <Route path="/combos" element={<Combos />} />
                        <Route path="/checkout" element={<Checkout />} />
                        <Route path="/order-success/:orderId" element={<OrderSuccess />} />
                        <Route path="/profile" element={<Profile />} />
                        <Route path="/orders" element={<Orders />} />
                        <Route path="/orders/:orderId/track" element={<OrderTracking />} />
                        <Route path="/terms" element={<TermsAndConditions />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password/:uid/:token" element={<ResetPassword />} />
                        <Route path="/surprise" element={<Surprise />} />
                      </Routes>
                    </Suspense>
                  </Layout>
                }
              />
            </Routes>
          </Suspense>
        </CartProvider>
      </AuthProvider>
    </Router>
    <Analytics />
    </GoogleOAuthProvider>
  );
}

export default App;
